
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../../styles/category.css";
import "../../styles/cartpop.css";
import { motion, AnimatePresence } from "framer-motion";
import { makeApi } from "../../api/callApi";
import Loader from "../../components/loader/loader";
import ProductCard from "./Productcard"; // Import the ProductCard component
import MiniCart from "./Minicart"; // Import the MiniCart component
import { LocalStorageUtils } from "../../utils/localstorage.utils.jsx";


function Subcategorypage() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [subservice, setSubservice] = useState();

  useEffect(() => {
    if (isInitialLoad) {
      window.scrollTo(0, 0);
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  // Fetch category products
  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      try {
        const response = await makeApi(
          `/api/get-products-by-subservice-id/${category}`,
          "GET"
        );
        if (response.data.subservicedata) {
          setSubservice(response.data.subservicedata);
        } else {
          const sortedProducts = response.data.products.sort(
            (a, b) => a.FinalPrice - b.FinalPrice
          );
          // Add default shop and price for products with shopPrices
          const updatedProducts = sortedProducts.map((product) => {
            if (product.shopPrices?.length > 0) {
              product.defaultShop = product.shopPrices[0].shopname;
              product.defaultPrice = product.shopPrices[0].price;
              product.defaultFinalPrice = product.shopPrices[0].FinalPrice;
            }
            return product;
          });
          setProducts(updatedProducts);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchCategories();
  }, [category]);

  // Load cart from localStorage on component mount
  useEffect(() => {
    const storedCart = LocalStorageUtils.getCart()
    setCart(storedCart);
  }, []);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    if (Object.keys(cart).length > 0) {
      LocalStorageUtils.setCart(cart);
    }
  }, [cart]);

  const getTotalCartValue = () => {
    return Object.values(cart).reduce(
      (total, item) => total + item.FinalPrice * item.quantity,
      0
    );
  };

  const clearCart = () => {
    setCart({});
    LocalStorageUtils.removeCart();
  };

  const handleAddToCart = (product, selectedShop) => {
    const shopDetails = product.shopPrices.find(
      (shop) => shop.shopname === selectedShop
    );
    setCart((prevCart) => ({
      ...prevCart,
      [product._id]: {
        ...product,
        shop: selectedShop,
        FinalPrice: shopDetails?.FinalPrice || product.defaultFinalPrice || product.FinalPrice,
        price: shopDetails?.price || product.defaultPrice || product.price,
        quantity: product.minQuantity || 1,
      },
    }));
  };

  const handleShopChange = (product, selectedShop) => {
    const shopDetails = product.shopPrices.find(
      (shop) => shop.shopname === selectedShop
    );
    setCart((prevCart) => ({
      ...prevCart,
      [product._id]: {
        ...prevCart[product._id],
        shop: selectedShop,
        FinalPrice: shopDetails?.FinalPrice || product.defaultFinalPrice || product.FinalPrice,
        price: shopDetails?.price || product.defaultPrice || product.price,
      },
    }));
  };

  const handleIncreaseQuantity = (product) => {
    setCart((prevCart) => ({
      ...prevCart,
      [product._id]: {
        ...prevCart[product._id],
        quantity: prevCart[product._id].quantity + 1,
      },
    }));
  };

  const handleDecreaseQuantity = (product) => {
    setCart((prevCart) => {
      const updatedCart = { ...prevCart };
      const existingProduct = prevCart[product._id];

      // Prevent quantity from going below minQuantity
      if (existingProduct.quantity > (product.minQuantity || 1)) {
        updatedCart[product._id].quantity -= 1;
      } else {
        delete updatedCart[product._id];
      }

      return updatedCart;
    });
  };

  const clearFromCart = (productId) => {
    setCart((prevCart) => {
      const updatedCart = { ...prevCart };
      delete updatedCart[productId];
      return updatedCart;
    });
  };

  // Filter products based on search query
  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {loading ? (
        <div className="loader-container">
          <Loader />
        </div>
      ) : (
        <div className="category-page">
          <h1 className="category-title">
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </h1>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          {subservice&& (
            <div className="services-container">
              {subservice.map((service) => (
                <motion.div
                  className="service-card"
                  key={service._id}
                  initial={{ scale: 0.8, y: 100, opecity: 0 }}
                  whileInView={{ scale: 1, y: 0, opecity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link
                    to={`/sub/category/${service.name.toLowerCase()}`}
                    className="service-link"
                  >
                    <img
                      src={service.image}
                      alt={service.name}
                      className="service-image"
                    />
                    <div className="service-details">
                      <div className="service-title">{service.name}</div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
          <div className="product-list">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                cart={cart}
                handleAddToCart={handleAddToCart}
                handleShopChange={handleShopChange}
                handleIncreaseQuantity={handleIncreaseQuantity}
                handleDecreaseQuantity={handleDecreaseQuantity}
                clearFromCart={clearFromCart}
              />
            ))}
            <div style={{ height: "50vh" }}>.</div>
          </div>

          {/* Mini Cart Popup */}
          <MiniCart
            cart={cart}
            clearCart={clearCart}
            getTotalCartValue={getTotalCartValue}
            clearFromCart={clearFromCart}
          />
        </div>
      )}
    </>
  );
}

export default Subcategorypage;